import {
  assertCustomFieldDraft,
  type QuoteLineWriteAuthorization,
  updateCustomFieldValue,
} from './customFieldValues';
import type { CpqState, CustomFieldOwner, CustomFieldValue } from './types';

export interface UnifiedFieldValueContext {
  quoteId?: string;
  quoteVersion?: number;
}

export interface UnifiedFieldValuePort {
  read(
    state: CpqState,
    owner: CustomFieldOwner,
    entityId: string,
    key: string,
    context?: UnifiedFieldValueContext,
  ): unknown;
  readAll(
    state: CpqState,
    owner: CustomFieldOwner,
    entityId: string,
    context?: UnifiedFieldValueContext,
  ): Record<string, CustomFieldValue>;
  write(
    state: CpqState,
    owner: CustomFieldOwner,
    entityId: string,
    key: string,
    value: CustomFieldValue | undefined,
    context?: UnifiedFieldValueContext,
  ): CpqState;
  replace(
    state: CpqState,
    owner: CustomFieldOwner,
    entityId: string,
    values: Record<string, CustomFieldValue> | undefined,
    context?: UnifiedFieldValueContext,
  ): CpqState;
}

function entityValues(
  state: CpqState,
  owner: CustomFieldOwner,
  entityId: string,
  quoteId?: string,
): Record<string, CustomFieldValue> | undefined {
  if (owner === 'device')
    return state.catalog.devices.find((record) => record.id === entityId)
      ?.customFieldValues;
  if (owner === 'package')
    return state.catalog.packages.find((record) => record.id === entityId)
      ?.customFieldValues;
  if (owner === 'plan')
    return state.catalog.plans.find((record) => record.id === entityId)
      ?.customFieldValues;
  if (owner === 'customer')
    return state.catalog.customers.find((record) => record.id === entityId)
      ?.customFieldValues;
  if (owner === 'quote-header') throw new Error('报价抬头不开放企业自定义字段');
  if (owner === 'quote-line') {
    const quote = quoteId ? state.quotes[quoteId] : undefined;
    return quote?.items.find((item) => item.itemId === entityId)
      ?.manualFieldValues;
  }
  return undefined;
}

function assertQuoteVersion(
  state: CpqState,
  owner: CustomFieldOwner,
  context?: UnifiedFieldValueContext,
) {
  if (owner !== 'quote-line') return;
  if (!context?.quoteId || context.quoteVersion === undefined)
    throw new Error('报价行必须绑定明确报价版本');
  if (state.quotes[context.quoteId]?.version !== context.quoteVersion)
    throw new Error('报价行版本不匹配');
}

function assertEntityExists(
  state: CpqState,
  owner: CustomFieldOwner,
  entityId: string,
  quoteId?: string,
) {
  if (
    owner === 'device' &&
    !state.catalog.devices.some((r) => r.id === entityId)
  )
    throw new Error('企业字段对象不存在');
  if (
    owner === 'package' &&
    !state.catalog.packages.some((r) => r.id === entityId)
  )
    throw new Error('企业字段对象不存在');
  if (owner === 'plan' && !state.catalog.plans.some((r) => r.id === entityId))
    throw new Error('企业字段对象不存在');
  if (
    owner === 'customer' &&
    !state.catalog.customers.some((r) => r.id === entityId)
  )
    throw new Error('企业字段对象不存在');
  if (owner === 'quote-header') throw new Error('报价抬头不开放企业自定义字段');
  if (owner === 'quote-line') {
    const quote = quoteId ? state.quotes[quoteId] : undefined;
    if (!quote?.items.some((item) => item.itemId === entityId))
      throw new Error('报价行不存在或不属于当前报价版本');
  }
}

/** Single disposable adapter used by object pages, formula evaluation and
 * channel preparation. It is deliberately a port over local state, not a
 * claim of a server repository or API. */
export function createDisposableFieldValuePort(
  options: { canWriteQuoteLine?: QuoteLineWriteAuthorization } = {},
): UnifiedFieldValuePort {
  return {
    read(state, owner, entityId, key, context) {
      assertQuoteVersion(state, owner, context);
      assertEntityExists(state, owner, entityId, context?.quoteId);
      return entityValues(state, owner, entityId, context?.quoteId)?.[key];
    },
    readAll(state, owner, entityId, context) {
      assertQuoteVersion(state, owner, context);
      assertEntityExists(state, owner, entityId, context?.quoteId);
      return structuredClone(
        entityValues(state, owner, entityId, context?.quoteId) ?? {},
      );
    },
    write(current, owner, entityId, key, value, context) {
      assertQuoteVersion(current, owner, context);
      assertEntityExists(current, owner, entityId, context?.quoteId);
      const next = updateCustomFieldValue(
        current,
        owner,
        entityId,
        key,
        value,
        context?.quoteId,
        context?.quoteVersion,
        options.canWriteQuoteLine,
      );
      return next;
    },
    replace(current, owner, entityId, values, context) {
      assertQuoteVersion(current, owner, context);
      assertEntityExists(current, owner, entityId, context?.quoteId);
      const before = entityValues(current, owner, entityId, context?.quoteId);
      assertCustomFieldDraft(
        current,
        owner,
        before,
        values,
        owner === 'quote-line' && options.canWriteQuoteLine
          ? {
              quote: context?.quoteId
                ? current.quotes[context.quoteId]
                : undefined,
              authorize: options.canWriteQuoteLine,
            }
          : undefined,
      );
      // A replace is intentionally transactional: validate the complete draft
      // before touching any record, then apply each key through the same write
      // command. If a later write fails the caller keeps the original state.
      let next = current;
      const keys = new Set([
        ...Object.keys(before ?? {}),
        ...Object.keys(values ?? {}),
      ]);
      for (const key of keys) {
        const value = values?.[key];
        if (JSON.stringify(before?.[key]) === JSON.stringify(value)) continue;
        next = updateCustomFieldValue(
          next,
          owner,
          entityId,
          key,
          value,
          context?.quoteId,
          context?.quoteVersion,
          options.canWriteQuoteLine,
        );
      }
      return next;
    },
  };
}

export function customFieldStoragePath(owner: CustomFieldOwner, key: string) {
  if (owner === 'quote-header') throw new Error('报价抬头不开放企业自定义字段');
  if (owner === 'quote-line') return `QuoteItem.manualFieldValues.${key}`;
  const record =
    owner === 'device'
      ? 'DeviceCatalogRecord'
      : owner === 'package'
        ? 'PackageCatalogRecord'
        : owner === 'plan'
          ? 'PlanCatalogRecord'
          : owner === 'customer'
            ? 'CustomerCatalogRecord'
            : 'CustomerCatalogRecord';
  return `${record}.customFieldValues.${key}`;
}
